import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MoreVertical } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { TapDisabledReason } from "@/components/ui/TapDisabledReason";
import { shouldIgnoreOpeningPointer } from "@/lib/touchOpenClose";
import {
  computeHorizontalMenuPanel,
  FLOATING_POPOVER_Z_INDEX,
  type AnchoredMenuPanelBox,
} from "@/lib/viewportAnchoredPopover";
import { cn } from "@/lib/utils";

const DOCK_ACTIONS_MENU_MIN_WIDTH = 280;

const DOCK_ACTIONS: ReadonlyArray<{
  id: string;
  label: string;
  run: (handlers: {
    onAmlLookup: () => void;
    onUnlistedSku: () => void;
    onNonSkuItem: () => void;
  }) => void;
}> = [
  {
    id: "aml-lookup",
    label: "AML lookup (full MPN)",
    run: (handlers) => handlers.onAmlLookup(),
  },
  {
    id: "unlisted-sku",
    label: "SKU not on PO",
    run: (handlers) => handlers.onUnlistedSku(),
  },
  {
    id: "non-sku",
    label: "Non-SKU item",
    run: (handlers) => handlers.onNonSkuItem(),
  },
];

export function InboundReceiveDockActionsMenu(props: {
  disabled: boolean;
  /** Shown when the operator taps More while it is disabled for a business rule. */
  disabledReason?: string | null;
  onAmlLookup: () => void;
  onUnlistedSku: () => void;
  onNonSkuItem: () => void;
}): JSX.Element {
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const openGuardRef = useRef<{ openedAtMs: number; pointerId: number | null }>({
    openedAtMs: 0,
    pointerId: null,
  });
  const openedByPointerRef = useRef(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuBox, setMenuBox] = useState<AnchoredMenuPanelBox | null>(null);

  const updateMenuPosition = useCallback(() => {
    const trigger = menuButtonRef.current;
    if (trigger == null) {
      setMenuBox(null);
      return;
    }
    setMenuBox(
      computeHorizontalMenuPanel({
        anchorRect: trigger.getBoundingClientRect(),
        menuWidth: Math.max(trigger.offsetWidth, DOCK_ACTIONS_MENU_MIN_WIDTH),
        horizontalAlign: "end",
        gap: 8,
        maxHeightCap: 320,
        preferPlacement: "below",
      }),
    );
  }, []);

  useEffect(() => {
    if (!menuOpen) {
      setMenuBox(null);
      return;
    }
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.visualViewport?.addEventListener("resize", onScrollOrResize);
    window.visualViewport?.addEventListener("scroll", onScrollOrResize);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.visualViewport?.removeEventListener("resize", onScrollOrResize);
      window.visualViewport?.removeEventListener("scroll", onScrollOrResize);
    };
  }, [menuOpen, updateMenuPosition]);

  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (menuButtonRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      if (
        shouldIgnoreOpeningPointer({
          openedAtMs: openGuardRef.current.openedAtMs,
          openingPointerId: openGuardRef.current.pointerId,
          nowMs: Date.now(),
          eventPointerId: event.pointerId,
          eventPointerType: event.pointerType,
        })
      ) {
        return;
      }
      setMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  const toggleMenu = (pointerId: number | null) => {
    if (props.disabled) return;
    setMenuOpen((open) => {
      const next = !open;
      if (next) {
        openGuardRef.current = { openedAtMs: Date.now(), pointerId };
      }
      return next;
    });
  };

  const runAction = (action: () => void) => {
    setMenuOpen(false);
    action();
  };

  const portalTarget = typeof document !== "undefined" ? document.body : null;

  const moreButton = (
      <Button
        ref={menuButtonRef}
        type="button"
        variant="secondary"
        className="min-h-11 gap-1.5 px-3 text-sm font-semibold touch-manipulation sm:min-h-12 sm:text-base"
        disabled={props.disabled}
        aria-label="More receiving actions"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          event.preventDefault();
          openedByPointerRef.current = true;
          toggleMenu(event.pointerId);
        }}
        onClick={(event) => {
          if (openedByPointerRef.current) {
            openedByPointerRef.current = false;
            event.preventDefault();
            return;
          }
          toggleMenu(null);
        }}
      >
        <MoreVertical className="h-5 w-5 shrink-0" aria-hidden />
        More
      </Button>
  );
  const disabledReason = props.disabledReason?.trim() ?? "";

  return (
    <>
      {disabledReason !== "" ? (
        <TapDisabledReason reason={disabledReason}>{moreButton}</TapDisabledReason>
      ) : (
        moreButton
      )}
      {menuOpen && menuBox != null && portalTarget != null
        ? createPortal(
            <div
              ref={panelRef}
              className="overflow-hidden rounded-lg border border-slate-200 bg-white py-1.5 shadow-xl"
              style={{
                position: "fixed",
                left: menuBox.left,
                top: menuBox.top,
                width: menuBox.width,
                zIndex: FLOATING_POPOVER_Z_INDEX,
              }}
              role="menu"
              aria-label="More receiving actions"
            >
              {DOCK_ACTIONS.map((action) => (
                <button
                  key={action.id}
                  type="button"
                  role="menuitem"
                  className={cn(
                    "flex min-h-12 w-full items-center px-4 text-left text-base font-medium touch-manipulation",
                    props.disabled
                      ? "cursor-not-allowed text-slate-400"
                      : "text-slate-800 active:bg-slate-100 hover:bg-slate-50",
                  )}
                  disabled={props.disabled}
                  onPointerDown={(event) => {
                    if (event.button !== 0 || props.disabled) return;
                    event.preventDefault();
                    runAction(() =>
                      action.run({
                        onAmlLookup: props.onAmlLookup,
                        onUnlistedSku: props.onUnlistedSku,
                        onNonSkuItem: props.onNonSkuItem,
                      }),
                    );
                  }}
                >
                  {action.label}
                </button>
              ))}
            </div>,
            portalTarget,
          )
        : null}
    </>
  );
}
